// ═══════════════════════════════════════════════════════════════
// IntegrateurWorkspace.js — « L'Intégrateur » (Pilot Mutation Phase 2)
// ═══════════════════════════════════════════════════════════════
// Workspace plein écran (overlay) qui donne le moteur d'action au cockpit :
// l'agent qui INGÈRE un article WordPress → le NETTOIE → le RÉ-ÉMET dans ITRIIA
// (project_files), sous contrôle humain (HITL). Le clic « Lancer l'Intégrateur »
// EST la porte HITL (sélection explicite). Cible republication site = tour suivant.
//   GAUCHE  : inventaire + cases à cocher + filtres + Aperçu (lecture nettoyée).
//   DROITE  : l'agent au travail (Ingestion → Nettoyage → Ré-émission) + résultat.
// ═══════════════════════════════════════════════════════════════

import React, { useState, useEffect, useCallback } from 'react';
import { X, Eye, FileText, File as FileIcon, Image as ImageIcon, Search, AlertTriangle, Cpu, CheckCircle2, Loader2 } from 'lucide-react';
import { pilotMutationApi } from '../../services/pilotMutationApi';

const ACCENT = '#0ea5e9'; // cyan « mutation »
const OK = '#10b981';

const TYPE_META = {
  post:       { single: 'Article', icon: FileText },
  page:       { single: 'Page',    icon: FileIcon },
  attachment: { single: 'Média',   icon: ImageIcon },
};
const STATUS_META = {
  discovered: { label: 'Découvert', color: '#64748b' },
  imported:   { label: 'Importé',   color: '#0ea5e9' },
  migrated:   { label: 'Migré',     color: '#10b981' },
  skipped:    { label: 'Ignoré',    color: '#a1a1aa' },
  error:      { label: 'Erreur',    color: '#ef4444' },
};
const STEPS = ['Ingestion', 'Nettoyage', 'Ré-émission'];

const card = { background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 12 };
const selStyle = { padding: '7px 9px', borderRadius: 8, border: '1px solid var(--card-border)', background: 'var(--input-bg, var(--card-bg))', color: 'var(--text)', fontSize: 12.5, cursor: 'pointer' };

export default function IntegrateurWorkspace({ projectId, canWrite = true, onClose, onImported }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('discovered');
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState(() => new Set());
  const [error, setError] = useState(null);

  const [running, setRunning] = useState(false);
  const [runStep, setRunStep] = useState(-1);
  const [result, setResult] = useState(null);

  const [preview, setPreview] = useState(null); // { loading, data, error, itemId }

  const loadItems = useCallback(async () => {
    try {
      const res = await pilotMutationApi.items(projectId, { type: filterType, status: filterStatus, q, limit: 200 });
      setItems(res.items || []);
    } catch (e) { setError(e?.response?.data?.error || e.message); }
  }, [projectId, filterType, filterStatus, q]);

  useEffect(() => { (async () => { setLoading(true); await loadItems(); setLoading(false); })(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { if (!loading) loadItems(); /* eslint-disable-next-line */ }, [filterType, filterStatus, q]);

  // Esc ferme (sauf si un aperçu est ouvert → ferme l'aperçu d'abord).
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') { if (preview) setPreview(null); else if (!running) onClose?.(); } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [preview, running, onClose]);

  const toggle = (id) => setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const visibleIds = items.map(it => it.id);
  const allSelected = visibleIds.length > 0 && visibleIds.every(id => selected.has(id));
  const toggleAll = () => setSelected(prev => {
    if (allSelected) { const n = new Set(prev); visibleIds.forEach(id => n.delete(id)); return n; }
    return new Set([...prev, ...visibleIds]);
  });

  const openPreview = async (itemId) => {
    setPreview({ loading: true, itemId });
    try {
      const res = await pilotMutationApi.preview(projectId, itemId);
      if (res.ok) setPreview({ loading: false, data: res.preview, itemId });
      else setPreview({ loading: false, error: res.error || 'Aperçu indisponible.', itemId });
    } catch (e) { setPreview({ loading: false, error: e?.response?.data?.message || e.message, itemId }); }
  };

  const runIntegrateur = async () => {
    const ids = [...selected];
    if (!ids.length || running) return;
    setRunning(true); setResult(null); setError(null);
    // Animation client des 3 étapes pendant le POST bloquant (v1).
    let step = 0; setRunStep(0);
    const timer = setInterval(() => { step = (step + 1) % STEPS.length; setRunStep(step); }, 900);
    try {
      const res = await pilotMutationApi.importBatch(projectId, ids);
      clearInterval(timer); setRunStep(STEPS.length); // tout coché
      setResult(res.ok ? res.summary : null);
      if (!res.ok) setError(res.error || 'Import échoué.');
      setSelected(new Set());
      await loadItems();
      onImported?.();
    } catch (e) {
      clearInterval(timer); setRunStep(-1);
      setError(e?.response?.data?.message || e?.response?.data?.error || e.message);
    } finally {
      setRunning(false);
    }
  };

  const selCount = selected.size;

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 120, background: 'var(--bg, #0e0c0a)', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 22px', borderBottom: '1px solid var(--card-border)', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
          <div style={{ width: 36, height: 36, borderRadius: 10, background: `${ACCENT}1a`, display: 'grid', placeItems: 'center' }}>
            <Cpu size={19} color={ACCENT} />
          </div>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>L'Intégrateur</div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Ingère → nettoie → ré-émet dans ITRIIA (réservoir de mutation)</div>
          </div>
        </div>
        <button onClick={() => !running && onClose?.()} disabled={running} title="Fermer (Échap)"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 12px', borderRadius: 8, border: '1px solid var(--card-border)', background: 'var(--card-bg)', color: 'var(--text-secondary)', fontSize: 13, cursor: running ? 'not-allowed' : 'pointer', opacity: running ? 0.5 : 1 }}>
          <X size={15} /> Fermer
        </button>
      </div>

      {/* Body 2 colonnes */}
      <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
        {/* GAUCHE — sélection */}
        <div style={{ flex: '1.5', minWidth: 0, display: 'flex', flexDirection: 'column', padding: '16px 18px', borderRight: '1px solid var(--card-border)' }}>
          {/* Filtres */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10 }}>
            <div style={{ position: 'relative', flex: '1 1 180px' }}>
              <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted, #6a6a85)' }} />
              <input value={q} onChange={e => setQ(e.target.value)} placeholder="Rechercher…"
                style={{ width: '100%', padding: '7px 10px 7px 30px', borderRadius: 8, border: '1px solid var(--card-border)', background: 'var(--input-bg, var(--card-bg))', color: 'var(--text)', fontSize: 12.5, boxSizing: 'border-box' }} />
            </div>
            <select value={filterType} onChange={e => setFilterType(e.target.value)} style={selStyle}>
              <option value="">Tous types</option>
              <option value="post">Articles</option>
              <option value="page">Pages</option>
            </select>
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={selStyle}>
              <option value="discovered">À importer</option>
              <option value="imported">Importés</option>
              <option value="skipped">Ignorés</option>
              <option value="error">Erreurs</option>
              <option value="">Tous statuts</option>
            </select>
          </div>

          {/* En-tête sélection */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', ...card, borderRadius: '10px 10px 0 0', borderBottom: 'none' }}>
            <input type="checkbox" checked={allSelected} onChange={toggleAll} disabled={!items.length} style={{ accentColor: ACCENT, cursor: 'pointer' }} />
            <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
              {selCount > 0 ? <b style={{ color: ACCENT }}>{selCount} sélectionné(s)</b> : `${items.length} item(s)`}
            </span>
          </div>

          {/* Liste */}
          <div style={{ ...card, borderRadius: '0 0 10px 10px', flex: 1, overflow: 'auto', minHeight: 0 }}>
            {loading && <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-secondary)', fontSize: 13 }}>Chargement…</div>}
            {!loading && items.length === 0 && <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-secondary)', fontSize: 13 }}>Aucun item pour ces filtres.</div>}
            {!loading && items.map(it => {
              const tm = TYPE_META[it.wp_type] || { single: it.wp_type, icon: FileText };
              const sm = STATUS_META[it.status] || { label: it.status, color: '#64748b' };
              const TIcon = tm.icon;
              const checked = selected.has(it.id);
              return (
                <div key={it.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderBottom: '1px solid var(--card-border)', fontSize: 13, background: checked ? `${ACCENT}0d` : 'transparent' }}>
                  <input type="checkbox" checked={checked} onChange={() => toggle(it.id)} style={{ accentColor: ACCENT, cursor: 'pointer' }} />
                  <TIcon size={13} style={{ color: 'var(--text-muted, #6a6a85)', flexShrink: 0 }} />
                  <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--text)' }} title={it.title || it.slug}>
                    {it.title || it.slug || `#${it.wp_id}`}
                    {it.is_elementor && <span style={{ marginLeft: 6, fontSize: 10, color: '#f59e0b', border: '1px solid #f59e0b55', borderRadius: 4, padding: '0 4px' }}>Elementor</span>}
                  </span>
                  <span style={{ fontSize: 10.5, fontWeight: 600, color: sm.color, background: `${sm.color}1a`, borderRadius: 5, padding: '2px 7px', flexShrink: 0 }}>{sm.label}</span>
                  <button onClick={() => openPreview(it.id)} title="Aperçu nettoyé"
                    style={{ display: 'inline-flex', alignItems: 'center', padding: 5, borderRadius: 6, border: '1px solid var(--card-border)', background: 'var(--card-bg)', color: ACCENT, cursor: 'pointer', flexShrink: 0 }}>
                    <Eye size={13} />
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {/* DROITE — l'Intégrateur au travail */}
        <div style={{ width: 360, flexShrink: 0, display: 'flex', flexDirection: 'column', padding: '16px 18px', overflow: 'auto' }}>
          <div style={{ fontSize: 12.5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--text-muted, #6a6a85)', marginBottom: 14 }}>L'Intégrateur</div>

          {/* 3 étapes */}
          <div style={{ ...card, padding: '14px 16px', marginBottom: 14 }}>
            {STEPS.map((label, i) => {
              const active = running && runStep === i;
              const done = (running && runStep > i) || (!running && result && runStep >= STEPS.length);
              const col = active ? ACCENT : done ? OK : 'var(--text-muted, #6a6a85)';
              return (
                <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0' }}>
                  <span style={{ width: 22, display: 'grid', placeItems: 'center' }}>
                    {active ? <Loader2 size={15} color={col} style={{ animation: 'spin 1s linear infinite' }} />
                      : done ? <CheckCircle2 size={15} color={col} />
                      : <span style={{ width: 9, height: 9, borderRadius: '50%', border: `2px solid ${col}` }} />}
                  </span>
                  <span style={{ fontSize: 13, color: active || done ? 'var(--text)' : 'var(--text-secondary)', fontWeight: active ? 600 : 400 }}>{label}</span>
                </div>
              );
            })}
          </div>

          {/* Bouton HITL */}
          <button onClick={runIntegrateur} disabled={!canWrite || selCount === 0 || running}
            style={{ width: '100%', padding: '11px 14px', borderRadius: 10, border: 'none',
              background: (!canWrite || selCount === 0 || running) ? 'var(--card-border)' : ACCENT,
              color: (!canWrite || selCount === 0 || running) ? 'var(--text-muted, #6a6a85)' : '#fff',
              fontSize: 13.5, fontWeight: 700, cursor: (!canWrite || selCount === 0 || running) ? 'not-allowed' : 'pointer',
              boxShadow: (!canWrite || selCount === 0 || running) ? 'none' : `0 2px 12px ${ACCENT}55`, transition: 'all .15s' }}>
            {running ? 'Intégration en cours…' : `Lancer l'Intégrateur${selCount ? ` (${selCount})` : ''}`}
          </button>
          {!canWrite && <div style={{ fontSize: 11.5, color: 'var(--text-muted, #6a6a85)', marginTop: 8, textAlign: 'center' }}>Lecture seule sur ce projet.</div>}

          {/* Résultat */}
          {result && (
            <div style={{ ...card, padding: '12px 14px', marginTop: 14, borderColor: `${OK}55`, background: `${OK}0d` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600, color: OK, marginBottom: 6 }}>
                <CheckCircle2 size={15} /> Intégration terminée
              </div>
              <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.7 }}>
                <div><b style={{ color: 'var(--text)' }}>{result.imported}</b> importé(s) dans le projet</div>
                {result.skipped > 0 && <div><b style={{ color: '#f59e0b' }}>{result.skipped}</b> ignoré(s) (Elementor — vague 2)</div>}
                {result.errored > 0 && <div><b style={{ color: '#ef4444' }}>{result.errored}</b> en erreur</div>}
              </div>
            </div>
          )}

          {error && (
            <div style={{ ...card, padding: '10px 12px', marginTop: 14, borderColor: '#ef444455', background: '#ef44440d', color: '#ef4444', fontSize: 12.5, display: 'flex', gap: 8, alignItems: 'flex-start' }}>
              <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 1 }} /> <span>{error}</span>
            </div>
          )}

          <div style={{ fontSize: 11.5, color: 'var(--text-muted, #6a6a85)', marginTop: 'auto', paddingTop: 16, lineHeight: 1.6 }}>
            Les articles importés deviennent des fichiers du projet (interrogeables, mémorisables). La republication vers un site est une étape ultérieure.
          </div>
        </div>
      </div>

      {/* Aperçu (lecture nettoyée) */}
      {preview && (
        <div onClick={() => setPreview(null)} style={{ position: 'fixed', inset: 0, zIndex: 130, background: 'rgba(0,0,0,.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
          <div onClick={e => e.stopPropagation()} style={{ ...card, width: 'min(760px, 94vw)', maxHeight: '88vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: '1px solid var(--card-border)' }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', display: 'flex', alignItems: 'center', gap: 8 }}>
                <Eye size={15} color={ACCENT} /> Aperçu nettoyé
                {preview.data?.is_elementor && <span style={{ fontSize: 10.5, color: '#f59e0b', border: '1px solid #f59e0b55', borderRadius: 4, padding: '1px 5px' }}>Elementor — ignoré à l'import</span>}
              </div>
              <button onClick={() => setPreview(null)} style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', display: 'grid', placeItems: 'center' }}><X size={17} /></button>
            </div>
            <div style={{ padding: '16px 20px', overflow: 'auto' }}>
              {preview.loading && <div style={{ textAlign: 'center', color: 'var(--text-secondary)', fontSize: 13, padding: 20 }}>Récupération & nettoyage…</div>}
              {preview.error && <div style={{ color: '#ef4444', fontSize: 13 }}>{preview.error}</div>}
              {preview.data && (
                <>
                  <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text)', marginBottom: 4 }}>{preview.data.title}</div>
                  <div style={{ fontSize: 11.5, color: 'var(--text-muted, #6a6a85)', marginBottom: 14 }}>
                    {preview.data.word_count} mots · {preview.data.old_url ? <a href={preview.data.old_url} target="_blank" rel="noreferrer" style={{ color: ACCENT }}>source</a> : 'sans URL'}
                  </div>
                  <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'inherit', fontSize: 13.5, lineHeight: 1.7, color: 'var(--text)', margin: 0 }}>{preview.data.html_clean}</pre>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <style>{`@keyframes spin { from { transform: rotate(0) } to { transform: rotate(360deg) } }`}</style>
    </div>
  );
}
